import React, {
  JSX,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router';
import useAuth from 'src/hooks/useAuth';
import { resolveSurvey } from 'src/services/aiFileUploadV2';
import { useAIFiles } from 'src/hooks/aiFileUploadV2/useAIFiles';
import { AIFileUploadV2WebSocketProvider } from 'src/contexts/AIFileUploadV2WebSocketProvider';
import type {
  PropertyContact,
  SurveyResolveResponse,
} from 'src/types/aiFileUploadV2';
import type { UseAIFilesResult } from 'src/hooks/aiFileUploadV2/useAIFiles';
import { DEFAULT_CONFIDENCE_THRESHOLD } from 'src/components/NewRenterInterface/AIFileUploadV2/constants';

interface AIFileUploadV2ContextValue {
  surveyId: number | null;
  targetQitId: number | null;
  targetAnswerGroupId: number | undefined;
  setTargetAnswerGroupId: (id: number) => void;
  context: SurveyResolveResponse['context'] | null;
  propertyContact: PropertyContact | null;
  recommendedDocuments: SurveyResolveResponse['recommended_documents'];
  confidenceThreshold: number;
  loading: boolean;
  error: string | null;
  refetch(): Promise<void>;
  files: UseAIFilesResult;
  /** Register additional WS message handlers (e.g. Apply All progress events). */
  registerWSHandlers(handlers: Record<string, (data: unknown) => void>): void;
  /** Remove previously registered handlers by their event-type keys. */
  unregisterWSHandlers(keys: string[]): void;
}

const AIFileUploadV2Context = createContext<AIFileUploadV2ContextValue | null>(
  null
);

export function useAIFileUploadV2Context(): AIFileUploadV2ContextValue {
  const ctx = useContext(AIFileUploadV2Context);
  if (!ctx) {
    throw new Error(
      'useAIFileUploadV2Context must be used inside AIFileUploadV2ContextProvider'
    );
  }
  return ctx;
}

export function useAIFileUploadV2ContextOptional(): AIFileUploadV2ContextValue | null {
  return useContext(AIFileUploadV2Context);
}

interface Props {
  children: ReactNode;
  certificationId?: number; // overrides URL param — used when embedded in interview flow
  userId?: number; // scope to a specific household member
  surveyId?: number; // pre-resolved survey id — skips resolveSurvey (interview embed)
  targetQitId?: number; // file QIT this upload session targets (interview embed)
  targetAnswerGroupId?: number; // answer group for multi-group scoping (interview embed)
  skipInitialFileFetch?: boolean; // brand-new multi-group instance: skip unscoped RenterFile fetch
}

export function AIFileUploadV2ContextProvider({
  children,
  certificationId: certificationIdProp,
  userId,
  surveyId: surveyIdProp,
  targetQitId,
  targetAnswerGroupId: targetAnswerGroupIdProp,
  skipInitialFileFetch,
}: Props): JSX.Element {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Prop takes priority over URL param (T2-11)
  const certificationId =
    certificationIdProp !== undefined
      ? certificationIdProp
      : searchParams.get('certification_id')
      ? Number(searchParams.get('certification_id'))
      : undefined;

  // Refs so resolve's useCallback dep array stays stable across navigations
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;

  const [surveyId, setSurveyId] = useState<number | null>(surveyIdProp ?? null);
  const [context, setContext] = useState<
    SurveyResolveResponse['context'] | null
  >(null);
  const [recommendedDocuments, setRecommendedDocuments] = useState<
    SurveyResolveResponse['recommended_documents']
  >([]);
  const [confidenceThreshold, setConfidenceThreshold] = useState<number>(
    DEFAULT_CONFIDENCE_THRESHOLD
  );
  const [propertyContact, setPropertyContact] =
    useState<PropertyContact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mutable answer group ID — can be set after lazy creation during file upload.
  const [targetAnswerGroupId, setTargetAnswerGroupId] = useState<
    number | undefined
  >(targetAnswerGroupIdProp);

  // Single useAIFiles instance for all routes under this layout
  // Only pass userId when viewing another user's files — no need to send it for own data.
  const targetUserId =
    userId !== undefined && user && userId !== user.id ? userId : undefined;
  const files = useAIFiles(
    surveyId,
    targetUserId,
    targetAnswerGroupId,
    targetQitId ?? null,
    skipInitialFileFetch
  );

  // Dynamic WS handler registry — components inside the tree can register
  // extra message handlers (e.g. Apply All progress) without restructuring
  // the provider. The ref is stable; useState causes re-render to propagate
  // the merged object down to the WS provider.
  const extraHandlersRef = useRef<Record<string, (data: unknown) => void>>({});
  const [extraHandlers, setExtraHandlers] = useState<
    Record<string, (data: unknown) => void>
  >({});

  const registerWSHandlers = useCallback(
    (handlers: Record<string, (data: unknown) => void>) => {
      extraHandlersRef.current = { ...extraHandlersRef.current, ...handlers };
      setExtraHandlers({ ...extraHandlersRef.current });
    },
    []
  );

  const unregisterWSHandlers = useCallback((keys: string[]) => {
    for (const k of keys) {
      delete extraHandlersRef.current[k];
    }
    setExtraHandlers({ ...extraHandlersRef.current });
  }, []);

  // Cache in-session — re-resolves only on user change (T2-4)
  const resolvedForUserRef = useRef<number | null>(null);

  // Embed mode (pre-resolved surveyId) skips resolveSurvey, so propertyContact
  // is normally null there. When a blocking failure lands in failedEntries we
  // lazily fetch the contact once so the interview flow can show the same
  // ContactInfoBanner as the standalone upload page (ENG-2838).
  const failedCount = Object.keys(files.failedEntries).length;
  const contactFetchedRef = useRef(false);
  const contactAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => contactAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (
      surveyIdProp === undefined ||
      certificationId === undefined ||
      failedCount === 0 ||
      contactFetchedRef.current
    ) {
      return;
    }
    contactFetchedRef.current = true;
    const controller = new AbortController();
    contactAbortRef.current = controller;
    resolveSurvey({ certificationId, userId, signal: controller.signal })
      .then((result) => {
        setPropertyContact(result.property_contact ?? null);
      })
      .catch(() => {
        // Best-effort fetch — the banner simply won't render on failure.
      });
  }, [surveyIdProp, certificationId, userId, failedCount]);

  const resolve = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!user) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await resolveSurvey({ certificationId, userId, signal });
        setSurveyId(result.survey_id);
        setContext(result.context);
        setPropertyContact(result.property_contact ?? null);
        setRecommendedDocuments(result.recommended_documents ?? []);
        setConfidenceThreshold(
          result.confidence_threshold ?? DEFAULT_CONFIDENCE_THRESHOLD
        );
        resolvedForUserRef.current = user.id;

        // T2-11: strip certification_id from the URL after resolution
        if (certificationId !== undefined) {
          const next = new URLSearchParams(searchParamsRef.current);
          next.delete('certification_id');
          const qs = next.toString();
          navigateRef.current(`${pathnameRef.current}${qs ? `?${qs}` : ''}`, {
            replace: true,
            state: locationStateRef.current,
          });
        }
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') {
          return;
        }
        setError('Failed to load questionnaire. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    // searchParams, navigate, location.pathname intentionally excluded —
    // they're accessed via refs to prevent resolve from being recreated on every navigation,
    // which would abort in-flight requests via the useEffect cleanup.
    [user, certificationId, userId]
  );

  useEffect(() => {
    if (surveyIdProp !== undefined) {
      // Pre-resolved (interview embed) — no survey resolution needed.
      setLoading(false);
      return;
    }
    if (!user) {
      return;
    }
    if (resolvedForUserRef.current === user.id && surveyId !== null) {
      return;
    }
    const controller = new AbortController();
    void resolve(controller.signal);
    return () => controller.abort();
  }, [user, resolve, surveyId, surveyIdProp]);

  const refetch = useCallback(async (): Promise<void> => {
    resolvedForUserRef.current = null;
    const controller = new AbortController();
    await resolve(controller.signal);
  }, [resolve]);

  return (
    <AIFileUploadV2Context.Provider
      value={{
        surveyId,
        targetQitId: targetQitId ?? null,
        targetAnswerGroupId,
        setTargetAnswerGroupId,
        context,
        propertyContact,
        recommendedDocuments,
        confidenceThreshold,
        loading,
        error,
        refetch,
        files,
        registerWSHandlers,
        unregisterWSHandlers,
      }}
    >
      <AIFileUploadV2WebSocketProvider
        onPatch={files.applyWSPatch}
        onStepCompleted={files.applyStepCompleted}
        onRefetch={files.refetch}
        extraMessageHandlers={extraHandlers}
      >
        {children}
      </AIFileUploadV2WebSocketProvider>
    </AIFileUploadV2Context.Provider>
  );
}
